import React, { useState, useEffect, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  DollarSign, 
  Download, 
  RefreshCw, 
  Clock,
  CheckCircle,
  AlertTriangle,
  Users,
  BarChart2,
  Database,
  Play
} from 'lucide-react';
import { format } from 'date-fns';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { AuthContext } from '../contexts/AuthContext';

const SpendImport = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [spendSummary, setSpendSummary] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [dbStatus, setDbStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  
  useEffect(() => {
    if (user && user.role === 'admin') {
      loadSpendData();
    }
  }, [user]);
  
  // Redirect non-admin users to dashboard
  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  const loadSpendData = async () => {
    try {
      setLoading(true);
      
      const [summaryRes, importsRes, statusRes] = await Promise.all([
        api.get(endpoints.spend.summary),
        api.get(endpoints.spend.imports),
        api.get(endpoints.spend.status)
      ]);
      
      setSpendSummary(summaryRes.data.summary || []);
      setImportHistory(importsRes.data.imports || []);
      setDbStatus(statusRes.data || null);
    } catch (error) {
      console.error('Failed to load spend data:', error);
      toast.error('Failed to load spend data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRunMigration = async () => {
    try {
      setMigrateLoading(true);
      
      const response = await api.post(endpoints.spend.migrate);
      
      if (response.data.success) {
        toast.success('Database migration completed successfully');
        // Reload data after successful migration
        await loadSpendData();
      } else {
        toast.error(response.data.message || 'Migration failed');
      }
    } catch (error) {
      console.error('Failed to run database migration:', error);
      toast.error('Failed to run database migration');
    } finally {
      setMigrateLoading(false);
    }
  };
  
  const handleImportSpend = async () => {
    try {
      setImportLoading(true);
      
      const response = await api.post(endpoints.spend.import);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Reload data after successful import
        await loadSpendData();
      } else {
        toast.error(response.data.message || 'Import failed');
      }
    } catch (error) {
      console.error('Failed to import spend data:', error);
      toast.error('Failed to import spend data');
    } finally {
      setImportLoading(false);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  };
  
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };
  
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'running':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };
  
  const getMonthName = (month) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[month - 1] || 'Unknown';
  };
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-8 h-8 text-green-600" />
            Cursor Spend Import
          </h1>
          <p className="text-gray-600 mt-1">
            Import and manage spending data from Cursor API
          </p>
        </div>
        
        <div className="mt-4 md:mt-0 flex items-center gap-3">
          <button
            onClick={loadSpendData}
            disabled={loading}
            className="btn-secondary btn-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          <button
            onClick={handleImportSpend}
            disabled={importLoading || !dbStatus || !dbStatus.tables || dbStatus.tables.length === 0}
            className="btn-primary btn-sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Import Current Month
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('summary')}
            className={`${
              activeTab === 'summary'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Monthly Summary
          </button>
          <button
            onClick={() => setActiveTab('imports')}
            className={`${
              activeTab === 'imports'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Import History
          </button>
          <button
            onClick={() => setActiveTab('database')}
            className={`${
              activeTab === 'database'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Database Status
          </button>
        </nav>
      </div>
      
      {/* Migration Alert */}
      {!loading && (!dbStatus || !dbStatus.tables || dbStatus.tables.length === 0) && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Database tables not found!</strong> You need to run the database migration first.
              </p>
              <div className="mt-4">
                <button
                  onClick={handleRunMigration}
                  disabled={migrateLoading}
                  className="btn-primary"
                >
                  <Database className={`w-4 h-4 mr-2 ${migrateLoading ? 'animate-spin' : ''}`} />
                  Run Database Migration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      ) : (
        <>
          {activeTab === 'summary' && (
            <div className="space-y-6">
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Monthly Spend Summary
                  </h2>
                  <p className="text-sm text-gray-600">
                    Overview of spend data by month
                  </p>
                </div>
                <div className="card-body overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Month
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Users
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Spend
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Requests
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tokens
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Updated
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {spendSummary.length > 0 ? (
                        spendSummary.map((month, index) => (
                          <tr key={`${month.Year}-${month.Month}`}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {getMonthName(month.Month)} {month.Year}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatNumber(month.UserCount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {formatCurrency(month.TotalSpend)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatNumber(month.TotalRequests)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatNumber(month.TotalTokens)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(month.LastImportDate)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                            No spend data available. Click "Import Current Month" to import data.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card bg-white shadow-sm rounded-lg overflow-hidden">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                        <Users className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Total Users
                          </dt>
                          <dd>
                            <div className="text-lg font-medium text-gray-900">
                              {spendSummary.length > 0 ? formatNumber(spendSummary[0]?.UserCount || 0) : '0'}
                            </div>
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card bg-white shadow-sm rounded-lg overflow-hidden">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                        <DollarSign className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Current Month Spend
                          </dt>
                          <dd>
                            <div className="text-lg font-medium text-gray-900">
                              {spendSummary.length > 0 ? formatCurrency(spendSummary[0]?.TotalSpend || 0) : '$0.00'}
                            </div>
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card bg-white shadow-sm rounded-lg overflow-hidden">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                        <BarChart2 className="h-6 w-6 text-purple-600" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Avg Spend Per User
                          </dt>
                          <dd>
                            <div className="text-lg font-medium text-gray-900">
                              {spendSummary.length > 0 && spendSummary[0]?.UserCount > 0
                                ? formatCurrency((spendSummary[0]?.TotalSpend || 0) / spendSummary[0]?.UserCount)
                                : '$0.00'}
                            </div>
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'imports' && (
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">
                  Import History
                </h2>
                <p className="text-sm text-gray-600">
                  History of spend data imports
                </p>
              </div>
              <div className="card-body overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date Range
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Records
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Started
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Completed
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created By
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {importHistory.length > 0 ? (
                      importHistory.map((item) => (
                        <tr key={item.ImportId}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(item.StartDate).split(',')[0]} - {formatDate(item.EndDate).split(',')[0]}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getStatusIcon(item.Status)}
                              <span className="ml-2 text-sm text-gray-900">
                                {item.Status.charAt(0).toUpperCase() + item.Status.slice(1)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatNumber(item.ProcessedRecords)} / {formatNumber(item.TotalRecords)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(item.StartedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.CompletedAt ? formatDate(item.CompletedAt) : 'In progress'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.CreatedBy || 'System'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                          No import history available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {activeTab === 'database' && (
            <div className="space-y-6">
              <div className="card">
                <div className="card-header flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Spend Database Status
                    </h2>
                    <p className="text-sm text-gray-600">
                      Status of spend data tables in the database
                    </p>
                  </div>
                  <button
                    onClick={handleRunMigration}
                    disabled={migrateLoading}
                    className="btn-primary btn-sm"
                  >
                    <Play className={`w-4 h-4 mr-2 ${migrateLoading ? 'animate-spin' : ''}`} />
                    Run Migration
                  </button>
                </div>
                <div className="card-body">
                  {dbStatus ? (
                    <div className="space-y-6">
                      <div className="overflow-x-auto">
                        <h3 className="text-md font-semibold mb-2">Database Tables</h3>
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Table Name
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Columns
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Indexes
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Foreign Keys
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Constraints
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Record Count
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {dbStatus.tables && dbStatus.tables.length > 0 ? (
                              dbStatus.tables.map((table) => (
                                <tr key={table.TableName}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {table.TableName}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {table.ColumnCount}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {table.IndexCount}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {table.ForeignKeyCount}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {(table.UniqueConstraintCount || 0) + (table.PrimaryKeyCount || 0)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {dbStatus.counts && dbStatus.counts[table.TableName] !== undefined
                                      ? formatNumber(dbStatus.counts[table.TableName])
                                      : 'N/A'}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                                  No spend database tables found. Click "Run Migration" to create them.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <Database className="h-5 w-5 text-blue-400" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-blue-700">
                              <strong>Spend data is stored in separate tables</strong> from the main analytics data.
                              This ensures that spend data is isolated and won't be affected by any data purges in the main tables.
                              If tables are missing, click "Run Migration" to create them.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <Database className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No database status available</h3>
                      <p className="mt-1 text-sm text-gray-500">Click refresh to check database status.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SpendImport;